import ResponseError from "#/types/responseError"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { carrotClient } from "#/utils/CarrotClient"
import { useQuery, UseQueryResult } from "@tanstack/react-query"

export enum CarrotPlanRequestType {
  INITIAL_INTAKE = "INITIAL_INTAKE",
  RENEWAL_INTAKE = "RENEWAL_INTAKE",
  JOURNEY_CHANGE_INTAKE = "JOURNEY_CHANGE_INTAKE",
  CHECK_IN_INTAKE = "CHECK_IN_INTAKE"
}

export const ROUTING_STATUS_QUERY_KEY = "in-progress-request-type"

interface InProgressCarrotPlanRequestData {
  requestType?: CarrotPlanRequestType
  message?: string
}

export function useGetInProgressCarrotPlanRequestType(): UseQueryResult<CarrotPlanRequestType | null, ResponseError> {
  return useGetInProgressCarrotPlanRequestTypeQuery((data) => data.requestType ?? null)
}

export function useGetInProgressCarrotPlanRequestTypeWithMessage(): UseQueryResult<
  string | CarrotPlanRequestType | null,
  ResponseError
> {
  return useGetInProgressCarrotPlanRequestTypeQuery((data) => (data.message || data.requestType) ?? null)
}

function useGetInProgressCarrotPlanRequestTypeQuery<TData>(
  select: (data: InProgressCarrotPlanRequestData) => TData
): UseQueryResult<TData, ResponseError> {
  return useQuery<InProgressCarrotPlanRequestData, ResponseError, TData>(
    [ROUTING_STATUS_QUERY_KEY],
    async (): Promise<InProgressCarrotPlanRequestData> => {
      const response = await carrotClient.getInProgressCarrotPlanRequestType()
      return parseInProgressRequestTypeResponse(response as InProgressCarrotPlanRequestData | null)
    },
    {
      retry: buildCarrotApiRetryPolicy(1),
      select
    }
  )
}

function parseInProgressRequestTypeResponse(
  response: InProgressCarrotPlanRequestData | null
): InProgressCarrotPlanRequestData {
  if (!response) {
    return { requestType: undefined, message: undefined }
  }

  if (hasInProgressRequest(response)) {
    return { requestType: response.requestType, message: undefined }
  } else if (doesNotHaveInProgressRequest(response)) {
    return { requestType: undefined, message: response.message }
  }

  return { requestType: undefined, message: undefined }
}

function hasInProgressRequest(
  response: InProgressCarrotPlanRequestData | null
): response is { requestType: CarrotPlanRequestType } {
  return response !== null && typeof response.requestType === "string" && response.requestType in CarrotPlanRequestType
}

function doesNotHaveInProgressRequest(
  response: InProgressCarrotPlanRequestData | null
): response is { message: string } {
  return response !== null && typeof response.message === "string"
}
